import Vue from "vue";
import Vuex, {
  ActionContext,
  ActionTree,
  GetterTree,
  MutationTree,
} from "vuex";
import { books } from "../books/store/index";
import { error } from "./modules/error";
import { web3 } from "./modules/web3";

import { Route } from "vue-router";
import {
  JWT_REFRESH_INTERVAL,
  PRODUCTION_MODE,
  USER_PROFILE_LOCALSOTORAGE_KEY,
} from "../globals";

import gql from "graphql-tag";
import jwtDecode from "jwt-decode";
import { apolloClient } from "../apollo";
import { clearJwt, getJwt, setJwt } from "../token";

import userFragment from "../gql/userFragment.graphql";
import { getLocal, isAdultDisabled } from "../lib/utils";

Vue.use(Vuex);

export type SearchContext =
  | "SHOP"
  | "INVENTORY"
  | "LIBRARY"
  | "WISH-LIST"
  | "LISTINGS"
  | "ACCOUNT";

export class RootState {
  public balance?: string = undefined;
  public footerHidden = false;
  public fps = 0;
  public fullscreenMenuOpen = false;
  public gameConnected = false;
  public isAdultDisabled: boolean = isAdultDisabled();
  public isError = false;
  public jwtExpired = false;
  public jwtRefreshCounter = 0;
  public loadingCounter = 0;
  public localUserProfile: any = undefined;
  public metaDescription = "";
  public metaKeyword = "";
  public receiveABP: any = "";
  public referralPoint: any = "";
  public route: Route;
  public searchContext: SearchContext = "SHOP";
  public searchContextRouterPath = "";
  public signinNextRoute: Route;
  public title = "";
  public user: any = undefined;
}

function appNameFromRoute(route) {
  if (!route) {
    return;
  }
  return route.path.split("/")[1];
}

const mutations: MutationTree<RootState> = {
  fullscreenMenuOpen(state: RootState, fullscreenMenuOpen: boolean) {
    state.fullscreenMenuOpen = fullscreenMenuOpen;
  },
  gameConnected(state: RootState, gameConnected: boolean) {
    state.gameConnected = gameConnected;
  },
  loading(state: RootState, loading: boolean) {
    state.loadingCounter += loading ? 1 : -1;
    state.loadingCounter = Math.max(0, state.loadingCounter);
  },
  signinNextRoute(state: RootState, signinNextRoute: Route) {
    if (signinNextRoute && signinNextRoute.name === "signup") {
      return;
    }
    state.signinNextRoute = signinNextRoute;
  },
  user(state: RootState, user: any) {
    state.user = user;
    state.balance = user && user.balance;

    web3.state.savedAccountAddress = user && user.wallet.address;
    web3.state.addressVerified = user && user.wallet.addressVerified;
  },
  searchContext(state: RootState, searchContext: SearchContext) {
    state.searchContext = searchContext.toUpperCase() as SearchContext;
  },
  searchContextRouterPath(state: RootState, path) {
    state.searchContextRouterPath = path;
  },

  incrementJWTRefreshCounter(state: RootState) {
    const nc = (state.jwtRefreshCounter + 1) % JWT_REFRESH_INTERVAL;
    state.jwtRefreshCounter = nc;
  },
  fps(state: RootState, fps: number) {
    state.fps = fps;
  },
  footerHidden(state: RootState, footerHidden: boolean) {
    state.footerHidden = footerHidden;
  },
  title(state: RootState, title: string) {
    state.title = title;
  },
  metaDescription(state: RootState, description: string) {
    state.metaDescription = description;
  },
  metaKeyword(state: RootState, keyword: string) {
    state.metaKeyword = keyword;
  },
  jwtExpired(state: RootState, jwtExpired: boolean) {
    state.jwtExpired = jwtExpired;
  },
  receiveABP(state: RootState, receiveABP: any) {
    state.receiveABP = receiveABP;
  },
  updateRP(state: RootState, referralPoint: any) {
    state.referralPoint = referralPoint;
  },
  updateError(state: RootState, errorState: boolean) {
    state.isError = errorState;
  },
  localUserProfile(state: RootState, localUserProfile: boolean) {
    state.localUserProfile = localUserProfile || {};
  },
};

const getters: GetterTree<RootState, any> = {
  isLoggedin(state: RootState) {
    return Boolean(state.user) && state.user.isActive;
  },
  username(state: RootState) {
    return state.user && state.user.username;
  },
  pixelDensity(state: RootState) {
    return devicePixelRatio;
  },
  appName(state: RootState) {
    if (!state.route) {
      return "";
    }
    return appNameFromRoute(state.route);
  },
};

const actions: ActionTree<RootState, any> = {
  async refreshJWT(context: ActionContext<RootState, any>) {
    console.log("refreshJWT");
    if (context.state.jwtRefreshCounter === 0) {
      const jwt = getJwt();
      if (!jwt) {
        return;
      }

      const oneSecMs = 1000;
      const decoded = jwtDecode(jwt);
      const expDate = new Date(decoded.exp * oneSecMs);
      const originDate = new Date(decoded.origIat * oneSecMs);
      const nowDate = new Date();
      const deltaTime = decoded.exp * oneSecMs - nowDate.getTime();

      console.log(decoded);
      console.log(
        "expDate",
        expDate,
        "\noriginD",
        originDate,
        "\nnow    ",
        nowDate,
      );
      console.log(deltaTime);

      if (deltaTime <= 0) {
        console.log("token expired");
        return;
      }

      let res;
      try {
        res = await apolloClient.mutate({
          mutation: gql`
            mutation RefreshToken($jwt: String!) {
              refreshToken(token: $jwt) {
                token
              }
            }
          `,
          variables: {
            jwt,
          },
        });
      } catch (error) {
        console.error(error);
        clearJwt();
        return;
      }
      setJwt(res.data.refreshToken.token);
    }
    context.commit("incrementJWTRefreshCounter");
  },
  fetchLocalUserProfile(context: ActionContext<RootState, any>) {
    const localUserProfile = getLocal(USER_PROFILE_LOCALSOTORAGE_KEY);
    context.commit("localUserProfile", localUserProfile);
  },
  async fetchUser(context: ActionContext<RootState, any>, route) {
    await context.dispatch("fetchLocalUserProfile");

    let data: any = {};

    const appName = appNameFromRoute(route) || context.getters.appName;

    const booksFragment =
      appName === "books"
        ? `
      preferredBookCategories {
        id
        isAdult
        name
        parentCategory
      }
      bookInfo{
        bookOnboardingComplete
      }
    `
        : "";

    const res = await apolloClient.query({
      query: gql`
        ${userFragment}
        query FetchUser {
          user {
            ...userFragment
            ${booksFragment}
          }
        }
      `,
      fetchPolicy: "no-cache",
    });
    data = res.data;

    if (
      res.errors &&
      res.errors[0].params &&
      res.errors[0].params.message === "Signature has expired"
    ) {
      context.commit("jwtExpired", true);
    }

    context.commit("user", data.user);

    const isLoggedin = context.getters.isLoggedin;

    if (isLoggedin) {
      await context.dispatch("refreshJWT");
    } else {
      clearJwt();
    }

    if (appName === "books") {
      await context.dispatch(`${appName}/syncUserPreferences`);
    }
  },
};

export default new Vuex.Store({
  modules: {
    web3,
    error,
    books,
  },
  strict: !PRODUCTION_MODE,
  state: new RootState(),
  mutations,
  getters,
  actions,
});
